import React, { useState, useMemo, useEffect, useRef } from 'react';
import { api } from '../../api';
import eftposStaffCancel from '../../lib/eftposStaffCancel';
import { printTyroCardReceipt, printTyroCertReceipts } from '../../lib/tyroReceiptPrint';

const { staffCancelNextAction } = eftposStaffCancel;

// Combined counter-payment modal.
//
// Wide-screen layout (landscape POS tablet / Windows POS):
//
//   +-------------------------------------------------+
//   | Take payment                    TOTAL $12.50    |
//   +----------------------+--------------------------+
//   |  CASH                |  CARD                    |
//   |                      |  2× Wrap        $20.00   |
//   |  Cash received ...   |  1× Coffee       $4.50   |
//   |  Change / Remaining  |  ┌────────────────────┐  |
//   |  [$5][$10]...[Exact] |  │ Tyro EFTPOS  $12.50│  |
//   |  [numpad with .]     |  └────────────────────┘  |
//   |  [Confirm cash $X]   |  hint text               |
//   +----------------------+--------------------------+
//
// Split tender: enter cash under the total → remaining shows on the card
// button → tap card → approve on Tyro for the remainder only.
// Full cash / full card paths are unchanged.
//
// On narrow screens the columns stack (cash on top, EFTPOS below).
//
// When provider is tyro_pay_instore, Card sends the exact amount to the
// DX8000 via Tyro Connect and polls until the webhook reports success.

const AU_NOTES = [5, 10, 20, 50, 100];
const MAX_PRESETS = 5;
const EFTPOS_POLL_MS = 1500;
const UNANSWERED_PROMPT_MS = 32_000;

function money(n) {
  return Math.round(n * 100) / 100;
}

// Compute cash preset chips tailored to the sale total. Rules:
//   1. Include every AU note denomination that on its own covers the total
//   2. For totals above $100, extend with successive multiples of $100
function computeCashPresets(total) {
  if (!Number.isFinite(total) || total <= 0) {
    return [5, 10, 20, 50, 100];
  }
  const values = [];
  for (const note of AU_NOTES) {
    if (note >= total - 0.005) values.push(note);
  }
  if (total > 100) {
    const ceilingHundred = Math.ceil(total / 100) * 100;
    const first = ceilingHundred > 100 ? ceilingHundred : 200;
    values.push(first, first + 100);
  }
  return values
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b)
    .slice(0, MAX_PRESETS);
}

function appendDigit(current, digit) {
  if (digit === '.') {
    if (current.includes('.')) return current;
    if (current === '') return '0.';
    return current + '.';
  }
  if (current === '0') return digit === '0' ? '0' : digit;
  const dotIdx = current.indexOf('.');
  if (dotIdx >= 0 && current.length - dotIdx > 2) return current;
  return current + digit;
}

function backspace(current) {
  return current.length <= 1 ? '' : current.slice(0, -1);
}

function PaymentCartSummary({ cart, pickupName }) {
  if (!Array.isArray(cart) || cart.length === 0) return null;
  const itemCount = cart.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
  const pickup = typeof pickupName === 'string' ? pickupName.trim() : '';
  return (
    <div className="pos-payment-cart-summary">
      <div className="pos-payment-cart-summary-head">
        <span>This sale</span>
        <span>
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </span>
      </div>
      {pickup ? (
        <div className="pos-payment-cart-pickup">Pickup: {pickup}</div>
      ) : null}
      <ul className="pos-payment-cart-lines">
        {cart.map((line) => {
          const qty = Number(line.quantity) || 1;
          const unit = Number(line.unitPrice) || 0;
          const name = line.item?.name || 'Item';
          return (
            <li key={line.lineKey || name} className="pos-payment-cart-line">
              <div className="pos-payment-cart-line-main">
                <span className="pos-payment-cart-line-name">
                  {qty}× {name}
                </span>
                <span className="pos-payment-cart-line-total">
                  ${(unit * qty).toFixed(2)}
                </span>
              </div>
              {line.optionsLabel ? (
                <div className="pos-payment-cart-line-opts">{line.optionsLabel}</div>
              ) : null}
              {line.comment ? (
                <div className="pos-payment-cart-line-opts">{line.comment}</div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function PosPaymentModal({
  total,
  cart = [],
  pickupName = '',
  provider = 'manual',
  certAutoReceipts = false,
  onCashConfirmed,
  onEftposApproved,
  onEftposIntegrated,
  onCancel,
}) {
  const [amountStr, setAmountStr] = useState('');
  // 'split' = combined cash + eftpos view. 'eftpos' = eftpos approval view.
  const [view, setView] = useState('split');
  const [pendingApproval, setPendingApproval] = useState(false);
  const [integratedPhase, setIntegratedPhase] = useState(null); // null | sending | waiting | prompt | error
  const [integratedStatus, setIntegratedStatus] = useState('');
  const [integratedError, setIntegratedError] = useState('');
  const [pendingDecision, setPendingDecision] = useState(null);
  const [answeringPrompt, setAnsweringPrompt] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [saleApproved, setSaleApproved] = useState(false);
  const [declinePrinting, setDeclinePrinting] = useState(false);
  const [declinePrintMsg, setDeclinePrintMsg] = useState('');
  const payRequestIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const answeredDecisionRef = useRef(null);
  const declineAutoPrintedRef = useRef(false);
  const completingRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const staffCancelRef = useRef(false);
  const leftAfterCancelRef = useRef(false);
  const returnToCartRef = useRef(() => {});
  const integrated = provider === 'tyro_pay_instore';

  const totalStr = total.toFixed(2);

  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  // Tyro drops an unanswered receipt question after ~30s. If our webhook is
  // late, hide the Yes/No so UnansweredQuestions can finish without a tap.
  useEffect(() => {
    const decisionId = pendingDecision?.decision_id;
    if (!decisionId) return undefined;
    const t = setTimeout(() => {
      if (answeredDecisionRef.current === decisionId) return;
      answeredDecisionRef.current = decisionId;
      setPendingDecision(null);
      setIntegratedPhase((phase) => (phase === 'prompt' ? 'waiting' : phase));
    }, UNANSWERED_PROMPT_MS);
    return () => clearTimeout(t);
  }, [pendingDecision?.decision_id]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const returnToCartAfterStaffCancel = () => {
    if (leftAfterCancelRef.current) return;
    leftAfterCancelRef.current = true;
    stopPolling();
    staffCancelRef.current = false;
    payRequestIdRef.current = null;
    onCancel();
  };
  returnToCartRef.current = returnToCartAfterStaffCancel;

  const presets = useMemo(() => computeCashPresets(total), [total]);

  const amountReceived = useMemo(() => {
    if (!amountStr) return 0;
    const n = parseFloat(amountStr);
    return Number.isFinite(n) ? n : 0;
  }, [amountStr]);

  const change = useMemo(
    () => money(amountReceived - total),
    [amountReceived, total]
  );

  const remainingOnCard = useMemo(
    () => money(Math.max(0, total - amountReceived)),
    [amountReceived, total]
  );

  const isSufficient = amountReceived + 1e-9 >= total;
  const isExact = amountStr !== '' && Math.abs(change) < 0.005;
  // Partial cash entered — card will take the remainder (split tender).
  const isPartialCash = amountStr !== '' && amountReceived > 1e-9 && !isSufficient;
  const cardChargeAmount = isPartialCash ? remainingOnCard : total;
  const cardChargeStr = cardChargeAmount.toFixed(2);

  const handlePreset = (value) => setAmountStr(value.toFixed(2));
  const handleExact = () => setAmountStr(totalStr);
  const handleClear = () => setAmountStr('');
  const handleKey = (k) => setAmountStr((prev) => appendDigit(prev, k));
  const handleBackspace = () => setAmountStr((prev) => backspace(prev));

  const handleConfirmCash = async () => {
    if (!isSufficient || pendingApproval) return;
    const amountPaid = money(amountReceived);
    const changeGiven = money(change);
    setPendingApproval(true);
    try {
      await onCashConfirmed(amountPaid, changeGiven);
    } finally {
      setPendingApproval(false);
    }
  };

  const handleBackToSplit = () => {
    if (pendingApproval || integratedPhase === 'waiting' || integratedPhase === 'sending') return;
    setView('split');
    setIntegratedPhase(null);
    setIntegratedError('');
  };

  const outcomeText = (status) => (
    (status && (status.result_text || status.progress_text)) || ''
  );

  const chargePayload = () => (
    isPartialCash
      ? { split: true, cashAmount: money(amountReceived), cardAmount: cardChargeAmount }
      : { split: false, cardAmount: cardChargeAmount }
  );

  const finishApprovedSale = async (status, payload) => {
    if (completingRef.current) return;
    completingRef.current = true;
    stopPolling();
    setSaleApproved(true);
    setPendingDecision(null);
    const approvedText = outcomeText(status);
    setIntegratedStatus(approvedText);
    const payRequestId = payRequestIdRef.current;
    const decisionId = status?.pending_decision?.decision_id;
    if (payRequestId && decisionId && answeredDecisionRef.current !== decisionId) {
      answeredDecisionRef.current = decisionId;
      void api.answerEftposPrompt(payRequestId, decisionId, 'NO').catch(() => {});
    }
    try {
      await onEftposApproved({
        ...(payload || chargePayload()),
        integrated: true,
        payRequestId,
        orders: status?.orders,
        dockets: status?.dockets,
        hasReceipts: Boolean(status?.has_receipts),
        resultText: approvedText,
      });
    } catch (err) {
      completingRef.current = false;
      setSaleApproved(true);
      setIntegratedStatus(err.message || approvedText || 'Approved — tap Continue');
    }
  };

  const showFailedOutcome = (text, payRequestId) => {
    setPendingDecision(null);
    setIntegratedPhase('error');
    setIntegratedStatus(text || '');
    setIntegratedError(text || '');
    setPendingApproval(false);
    if (certAutoReceipts && payRequestId && !declineAutoPrintedRef.current) {
      declineAutoPrintedRef.current = true;
      setDeclinePrintMsg('Printing decline receipts…');
      setDeclinePrinting(true);
      printTyroCertReceipts(payRequestId)
        .then(() => setDeclinePrintMsg('Decline receipts sent to printer.'))
        .catch((err) => setDeclinePrintMsg(err.message || 'Auto-print failed — use Print card receipt'))
        .finally(() => setDeclinePrinting(false));
    }
  };

  const startIntegratedCharge = async () => {
    if (!onEftposIntegrated || pendingApproval) return;
    setPendingApproval(true);
    setIntegratedPhase('sending');
    setIntegratedError('');
    setPendingDecision(null);
    setDeclinePrintMsg('');
    setCancelling(false);
    setSaleApproved(false);
    staffCancelRef.current = false;
    leftAfterCancelRef.current = false;
    payRequestIdRef.current = null;
    declineAutoPrintedRef.current = false;
    answeredDecisionRef.current = null;
    completingRef.current = false;
    pollInFlightRef.current = false;
    setIntegratedStatus(`Sending $${cardChargeStr} to the DX8000…`);
    try {
      const payload = chargePayload();
      const result = await onEftposIntegrated(payload);
      const payRequestId = result?.pay_request_id;
      if (!payRequestId) throw new Error('No pay request returned from server');
      payRequestIdRef.current = payRequestId;
      setIntegratedPhase('waiting');
      setIntegratedStatus('Waiting for customer on the terminal…');

      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        if (completingRef.current || pollInFlightRef.current) return;
        pollInFlightRef.current = true;
        try {
          const status = await api.getEftposStatus(payRequestId);
          if (!status.pending_decision?.decision_id) {
            setPendingDecision(null);
          }
          if (status.approved) {
            staffCancelRef.current = false;
            await finishApprovedSale(status, payload);
            return;
          }
          const action = staffCancelNextAction({
            staffCancel: staffCancelRef.current,
            approved: Boolean(status.approved),
            failed: Boolean(status.failed),
          });
          if (action === 'return_to_cart') {
            stopPolling();
            void api.cancelEftpos(payRequestId).catch(() => {});
            returnToCartRef.current();
            return;
          }
          if (action === 'show_failed') {
            stopPolling();
            showFailedOutcome(outcomeText(status), payRequestId);
            if (status.pending_decision?.decision_id
              && answeredDecisionRef.current !== status.pending_decision.decision_id) {
              setPendingDecision(status.pending_decision);
            }
            return;
          }
          if (action === 'keep_cancelling') {
            setPendingDecision(null);
            setIntegratedPhase((phase) => (phase === 'prompt' ? 'waiting' : phase));
            setIntegratedStatus('Cancelling… waiting for the terminal');
            return;
          }
          if (status.pending_decision?.decision_id
            && answeredDecisionRef.current !== status.pending_decision.decision_id) {
            setPendingDecision(status.pending_decision);
            setIntegratedPhase('prompt');
            setIntegratedStatus(
              outcomeText(status)
              || status.pending_decision.question
              || 'Terminal needs an answer'
            );
          } else if (status.progress_text) {
            setIntegratedStatus(status.progress_text);
          } else if (status.result_text) {
            setIntegratedStatus(status.result_text);
          } else if (status.tyro_status) {
            setIntegratedStatus(status.tyro_status);
          }
        } catch (err) {
          console.warn('[pos] eftpos poll error:', err.message);
        } finally {
          pollInFlightRef.current = false;
        }
      }, EFTPOS_POLL_MS);
    } catch (err) {
      stopPolling();
      setIntegratedPhase('error');
      setIntegratedError(err.message || 'Failed to start terminal payment');
      setPendingApproval(false);
    }
  };

  // Pay Instore: one tap sends to the DX8000 and stays on Take payment
  // (no secondary EFTPOS screen). Manual mode still opens approve/decline.
  const handleTapEftpos = () => {
    if (integrated) {
      void startIntegratedCharge();
      return;
    }
    setView('eftpos');
  };

  const integratedBusy = integrated && (
    integratedPhase === 'sending'
    || integratedPhase === 'waiting'
    || integratedPhase === 'prompt'
    || cancelling
  );

  const handleAnswerPrompt = async (answer) => {
    const payRequestId = payRequestIdRef.current;
    const decisionId = pendingDecision?.decision_id;
    if (!payRequestId || !decisionId || answeringPrompt) return;
    setAnsweringPrompt(true);
    try {
      await api.answerEftposPrompt(payRequestId, decisionId, answer);
      answeredDecisionRef.current = decisionId;
      setPendingDecision(null);
      setIntegratedPhase('waiting');
      setIntegratedStatus('Continuing on terminal…');
      // Printing is never triggered by answering YES — staff use Print receipt
      // on the completion screen when the customer wants paper.
    } catch (err) {
      setIntegratedStatus(err.message || 'Failed to answer terminal prompt');
    } finally {
      setAnsweringPrompt(false);
    }
  };

  const handleCancelIntegrated = async () => {
    const id = payRequestIdRef.current;
    if (saleApproved && id) {
      try {
        const status = await api.getEftposStatus(id);
        await finishApprovedSale(status, chargePayload());
      } catch {
        stopPolling();
        onCancel();
      }
      return;
    }
    if (!id || integratedPhase === 'error') {
      stopPolling();
      payRequestIdRef.current = null;
      setIntegratedPhase(null);
      setIntegratedError('');
      setPendingApproval(false);
      setCancelling(false);
      onCancel();
      return;
    }
    if (cancelling || staffCancelRef.current) return;
    staffCancelRef.current = true;
    setCancelling(true);
    setPendingDecision(null);
    setIntegratedPhase((phase) => (phase === 'prompt' ? 'waiting' : phase));
    setIntegratedStatus('Cancelling…');
    try {
      const result = await api.cancelEftpos(id);
      const action = staffCancelNextAction({
        staffCancel: true,
        approved: Boolean(result?.approved),
        failed: Boolean(result?.failed),
      });
      if (action === 'complete_sale') {
        staffCancelRef.current = false;
        setCancelling(false);
        try {
          const status = await api.getEftposStatus(id);
          await finishApprovedSale(status, chargePayload());
        } catch {
          setSaleApproved(true);
          setIntegratedStatus(outcomeText(result) || result.tyro_status || '');
        }
      } else if (action === 'return_to_cart') {
        returnToCartRef.current();
      } else {
        setIntegratedStatus('Cancelling… waiting for the terminal');
      }
    } catch (err) {
      setIntegratedStatus(err.message || 'Cancelling… waiting for the terminal');
    }
  };

  const handleApproveEftpos = async () => {
    if (integrated) {
      await startIntegratedCharge();
      return;
    }
    if (pendingApproval) return;
    setPendingApproval(true);
    try {
      if (isPartialCash) {
        await onEftposApproved({
          split: true,
          cashAmount: money(amountReceived),
          cardAmount: cardChargeAmount,
        });
      } else {
        await onEftposApproved({ split: false, cardAmount: total });
      }
    } finally {
      setPendingApproval(false);
    }
  };

  const backdropClickHandler = (pendingApproval || integratedBusy || integratedPhase === 'prompt')
    ? undefined
    : onCancel;

  const changeClass = !amountStr
    ? ''
    : isSufficient
    ? 'pos-tender-change-ok'
    : isPartialCash
    ? 'pos-tender-change-remain'
    : 'pos-tender-change-short';

  const changeLabel = !amountStr
    ? 'Change'
    : isSufficient
    ? isExact
      ? 'Exact — no change'
      : 'Change to give'
    : isPartialCash
    ? 'Remaining on card'
    : 'Short';

  const changeValue = !amountStr
    ? '$0.00'
    : isSufficient
    ? `$${change.toFixed(2)}`
    : `$${remainingOnCard.toFixed(2)}`;

  return (
    <div className="pos-modal-backdrop" onClick={backdropClickHandler}>
      <div
        className="pos-modal pos-payment-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pos-payment-header">
          <h2 className="pos-modal-title">
            {view === 'eftpos' && !integrated ? 'EFTPOS payment' : 'Take payment'}
          </h2>
          <div>
            <span className="pos-payment-total-inline-label">Total</span>
            <span className="pos-payment-total-inline">${totalStr}</span>
          </div>
        </div>

        {view === 'split' && (
          <>
            <div className={`pos-payment-cols${integratedBusy || integratedPhase === 'error' ? ' pos-payment-cols-terminal' : ''}`}>
              <div className={`pos-payment-col pos-payment-cash-col${integratedBusy ? ' is-disabled' : ''}`}>
                <h3 className="pos-payment-col-heading">Cash</h3>

                <div className="pos-tender-input-row">
                  <div className="pos-tender-input-label">Received</div>
                  <div className="pos-tender-input-value">
                    ${amountStr || '0.00'}
                  </div>
                </div>

                <div className={`pos-tender-change ${changeClass}`}>
                  <div>{changeLabel}</div>
                  <div className="pos-tender-change-value">{changeValue}</div>
                </div>

                <button
                  type="button"
                  className="pos-tender-exact-btn"
                  onClick={handleExact}
                  disabled={integratedBusy}
                >
                  Exact ${totalStr}
                </button>

                <div className="pos-tender-chips">
                  {presets.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="pos-tender-chip"
                      onClick={() => handlePreset(v)}
                      disabled={integratedBusy}
                    >
                      ${v}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="pos-tender-chip"
                    onClick={handleClear}
                    disabled={integratedBusy}
                  >
                    Clr
                  </button>
                </div>

                <div className="pos-tender-numpad">
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('1')} disabled={integratedBusy}>1</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('2')} disabled={integratedBusy}>2</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('3')} disabled={integratedBusy}>3</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('4')} disabled={integratedBusy}>4</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('5')} disabled={integratedBusy}>5</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('6')} disabled={integratedBusy}>6</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('7')} disabled={integratedBusy}>7</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('8')} disabled={integratedBusy}>8</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('9')} disabled={integratedBusy}>9</button>
                  <button type="button" className="pos-tender-key pos-tender-key-fn" onClick={() => handleKey('.')} disabled={integratedBusy}>.</button>
                  <button type="button" className="pos-tender-key" onClick={() => handleKey('0')} disabled={integratedBusy}>0</button>
                  <button type="button" className="pos-tender-key pos-tender-key-fn" onClick={handleBackspace} disabled={integratedBusy}>&larr;</button>
                </div>

                <button
                  type="button"
                  className="pos-payment-confirm-cash"
                  onClick={handleConfirmCash}
                  disabled={!isSufficient || pendingApproval || integratedBusy}
                >
                  {pendingApproval && view === 'split'
                    ? 'Recording…'
                    : !amountStr
                    ? 'Enter cash received'
                    : isSufficient
                    ? `Confirm cash • change $${change.toFixed(2)}`
                    : isPartialCash
                    ? `Cash $${amountReceived.toFixed(2)} — tap Card for rest`
                    : `Short $${Math.abs(change).toFixed(2)}`}
                </button>
              </div>

              <div className="pos-payment-col pos-payment-eftpos-col">
                <h3 className="pos-payment-col-heading">Card</h3>
                <PaymentCartSummary cart={cart} pickupName={pickupName} />
                {integrated && (integratedBusy || integratedPhase === 'error') ? (
                  <div className="pos-terminal-live">
                    <div className="pos-terminal-live-amount">${cardChargeStr}</div>
                    <div className={`pos-terminal-live-status${integratedPhase === 'error' ? ' is-failed' : ''}`}>
                      {integratedPhase === 'error'
                        ? (integratedError || integratedStatus || '')
                        : (integratedStatus || 'Working…')}
                    </div>
                    {integratedPhase === 'prompt' && pendingDecision && !cancelling && (
                      <div className="pos-eftpos-prompt">
                        <div className="pos-eftpos-prompt-q">
                          {pendingDecision.question || 'Would you like a receipt?'}
                        </div>
                        <div className="pos-eftpos-prompt-actions">
                          {(Array.isArray(pendingDecision.options) && pendingDecision.options.length
                            ? pendingDecision.options
                            : ['YES', 'NO']
                          ).map((opt) => {
                            const label = typeof opt === 'string' ? opt : (opt?.label || opt?.value || String(opt));
                            const value = typeof opt === 'string' ? opt : (opt?.value || opt?.label || String(opt));
                            return (
                              <button
                                key={value}
                                type="button"
                                className="pos-modal-btn pos-modal-btn-secondary"
                                disabled={answeringPrompt}
                                onClick={() => handleAnswerPrompt(value)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="pos-terminal-live-actions">
                      {integratedPhase === 'error' && payRequestIdRef.current && (
                        <button
                          type="button"
                          className="pos-modal-btn pos-modal-btn-secondary"
                          disabled={declinePrinting}
                          onClick={async () => {
                            const id = payRequestIdRef.current;
                            if (!id || declinePrinting) return;
                            setDeclinePrinting(true);
                            setDeclinePrintMsg('');
                            try {
                              await printTyroCardReceipt(id, {
                                which: certAutoReceipts ? 'all' : 'customer',
                              });
                              setDeclinePrintMsg('Card receipt sent to printer.');
                            } catch (err) {
                              setDeclinePrintMsg(err.message || 'Print failed');
                            } finally {
                              setDeclinePrinting(false);
                            }
                          }}
                        >
                          {declinePrinting ? 'Printing…' : 'Print card receipt'}
                        </button>
                      )}
                      {integratedPhase === 'error' && !cancelling && (
                        <button
                          type="button"
                          className="pos-modal-btn pos-modal-btn-success"
                          onClick={() => {
                            declineAutoPrintedRef.current = false;
                            setDeclinePrintMsg('');
                            void startIntegratedCharge();
                          }}
                        >
                          Retry
                        </button>
                      )}
                      <button
                        type="button"
                        className="pos-modal-btn pos-modal-btn-danger"
                        onClick={handleCancelIntegrated}
                        disabled={integratedPhase === 'sending' || answeringPrompt || cancelling}
                      >
                        {cancelling
                          ? 'Cancelling…'
                          : saleApproved
                          ? 'Continue'
                          : integratedPhase === 'error'
                          ? 'Close'
                          : 'Cancel payment'}
                      </button>
                    </div>
                    {declinePrintMsg && (
                      <div className="pos-terminal-live-error" style={{ marginTop: 8 }}>
                        {declinePrintMsg}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="pos-payment-eftpos-btn"
                      onClick={handleTapEftpos}
                      disabled={pendingApproval}
                    >
                      <span className="pos-payment-eftpos-icon" aria-hidden>&#128179;</span>
                      <span className="pos-payment-eftpos-label">
                        {isPartialCash ? 'Tyro EFTPOS (split)' : 'Tyro EFTPOS'}
                      </span>
                      <span className="pos-payment-eftpos-amount">${cardChargeStr}</span>
                      {isPartialCash && (
                        <span className="pos-payment-eftpos-split-note">
                          Cash ${amountReceived.toFixed(2)} + Card ${cardChargeStr}
                        </span>
                      )}
                    </button>
                    <div className="pos-payment-eftpos-hint">
                      {isPartialCash
                        ? `Charge $${cardChargeStr} on the terminal for the balance after cash.`
                        : integrated
                        ? 'Tap once — exact amount goes straight to the DX8000.'
                        : provider === 'tyro'
                        ? 'Sends the sale straight to the Tyro terminal.'
                        : 'Enter the amount on the DX8000, then tap to confirm approval.'}
                    </div>
                  </>
                )}
              </div>
            </div>

            {!(integrated && (integratedBusy || integratedPhase === 'error')) && (
              <div className="pos-payment-footer">
                <button
                  type="button"
                  className="pos-modal-btn pos-modal-btn-secondary"
                  onClick={onCancel}
                  disabled={pendingApproval}
                >
                  Cancel sale
                </button>
              </div>
            )}
          </>
        )}

        {view === 'eftpos' && (
          <>
            <div className="pos-eftpos-body">
              <div>Amount to charge</div>
              <div className="pos-eftpos-amount">${cardChargeStr}</div>

              {isPartialCash && (
                <div className="pos-eftpos-split-summary">
                  Cash ${amountReceived.toFixed(2)} already taken · Card ${cardChargeStr}
                </div>
              )}

              {integrated && (
                <div className="pos-eftpos-instruction">
                  {integratedPhase === 'waiting' || integratedPhase === 'sending'
                    ? (integratedStatus || 'Working…')
                    : integratedPhase === 'error'
                    ? null
                    : (
                      <>
                        Sending <strong>${cardChargeStr}</strong> to the DX8000…
                        Customer pays on the terminal. Kitchen dockets print when approved;
                        card receipts print only if you request them.
                      </>
                    )}
              {integratedError && (
                    <div className="pos-terminal-live-error">{integratedError}</div>
                  )}
                </div>
              )}

              {integrated && integratedPhase === 'prompt' && pendingDecision && !cancelling && (
                <div className="pos-eftpos-prompt">
                  <div className="pos-eftpos-prompt-q">
                    {pendingDecision.question || 'Would you like a receipt?'}
                  </div>
                  <div className="pos-eftpos-prompt-hint">
                    Card receipts are not printed automatically. Choose an answer for the
                    terminal; use <strong>Print card receipt</strong> after the sale if needed.
                  </div>
                  <div className="pos-eftpos-prompt-actions">
                    {(Array.isArray(pendingDecision.options) && pendingDecision.options.length
                      ? pendingDecision.options
                      : ['YES', 'NO']
                    ).map((opt) => {
                      const label = typeof opt === 'string' ? opt : (opt?.label || opt?.value || String(opt));
                      const value = typeof opt === 'string' ? opt : (opt?.value || opt?.label || String(opt));
                      return (
                        <button
                          key={value}
                          type="button"
                          className="pos-modal-btn pos-modal-btn-secondary"
                          disabled={answeringPrompt}
                          onClick={() => handleAnswerPrompt(value)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {provider === 'manual' && (
                <div className="pos-eftpos-instruction">
                  1. Type <strong>${cardChargeStr}</strong> on the DX8000.<br />
                  2. Present the card / phone.<br />
                  3. Wait for approval on the terminal.<br />
                  4. Tap the matching button below.
                </div>
              )}

              {pendingApproval && !integrated && (
                <div className="pos-eftpos-status">Recording sale — do not close this window.</div>
              )}
              {integrated && (integratedPhase === 'waiting' || integratedPhase === 'sending' || integratedPhase === 'prompt') && (
                <div className="pos-eftpos-status">{integratedStatus}</div>
              )}
            </div>

            <div className="pos-modal-actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="pos-payment-back"
                onClick={handleBackToSplit}
                disabled={pendingApproval && integratedPhase !== 'error'}
              >
                &larr; Back
              </button>
              <button
                type="button"
                className="pos-modal-btn pos-modal-btn-danger"
                onClick={integrated && payRequestIdRef.current ? handleCancelIntegrated : onCancel}
                disabled={integratedPhase === 'sending' || answeringPrompt || cancelling}
              >
                {cancelling
                  ? 'Cancelling…'
                  : integrated && saleApproved
                  ? 'Continue'
                  : integrated && integratedPhase === 'error'
                  ? 'Close'
                  : integrated && (integratedPhase === 'waiting' || integratedPhase === 'prompt')
                  ? 'Cancel payment'
                  : 'Declined / Cancel'}
              </button>
              {/* Integrated: charge starts from the blue Tyro button — only show
                  Retry here on failure. Manual mode still needs Approved. */}
              {integrated ? (
                integratedPhase === 'error' && (
                  <button
                    type="button"
                    className="pos-modal-btn pos-modal-btn-success"
                    onClick={handleApproveEftpos}
                    style={{ flex: 1, minWidth: 200 }}
                  >
                    Retry send to terminal
                  </button>
                )
              ) : (
                <button
                  type="button"
                  className="pos-modal-btn pos-modal-btn-success"
                  onClick={handleApproveEftpos}
                  disabled={pendingApproval}
                  style={{ flex: 1, minWidth: 200 }}
                >
                  {pendingApproval ? 'Recording…' : 'Approved'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
